import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiPost, operationsClient } from '@/lib/api';
import { leadEndpoints } from '@/lib/api/endpoints';
import { leadKeys } from '@/lib/queryKeys';
import { useApiMutation } from '../useApiMutation';
import type { BulkAutoAssignLeadsDto, BulkAutoAssignLeadsResult } from '@/types/lead';

/**
 * Runs the assignment engine across many leads (`POST /leads/bulk-auto-assign`).
 * Mirrors `useBulkAssignLeads`'s invalidations and seeds each returned lead
 * into its detail cache.
 */
export function useBulkAutoAssignLeads() {
  const queryClient = useQueryClient();

  return useApiMutation<BulkAutoAssignLeadsResult, BulkAutoAssignLeadsDto>({
    mutationFn: (dto) =>
      apiPost<BulkAutoAssignLeadsResult, BulkAutoAssignLeadsDto>(
        operationsClient,
        leadEndpoints.bulkAutoAssign(),
        dto,
      ),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: leadKeys.pipeline() });
      // Invalidate (not setQueryData) so an opened detail refetches via
      // GET /leads/:id, the only path that resolves departed-owner names
      // (NOT-486).
      data.results.forEach((result) => {
        void queryClient.invalidateQueries({ queryKey: leadKeys.detail(result.leadId) });
      });
      toast.success(
        `${data.assigned} of ${data.requested} lead${data.requested === 1 ? '' : 's'} assigned`,
      );
    },
  });
}
